/**
 * FinanceDashboard — main container for the Finance view.
 *
 * Layout:
 * - Financial Summary Cards (5 KPIs)
 * - Investment Table (full-width sortable table)
 * - Investment Portfolio Bubble Chart
 *
 * Resource allocation moved to dedicated Resources view (WQ-043).
 */

import { useMemo } from 'react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import { nodePassesFilter } from '../../utils/filter-nodes';
import { FinancialSummaryCards } from './FinancialSummaryCards';
import { InvestmentTable } from './InvestmentTable';
import { InvestmentPortfolioChart } from './InvestmentPortfolioChart';
import { useFteAllocations } from '../../hooks/useFteAllocations';

export function FinanceDashboard() {
  const { nodes, filters, resourceConfig, updateResourceConfig } = useAdjacencyStore();

  const fteAllocations = useFteAllocations();
  const discountRate = resourceConfig.npvDiscountRate;
  const periodYears = resourceConfig.npvPeriodYears;

  // Apply shared filters
  const allFiltered = useMemo(
    () => nodes.filter((n) => nodePassesFilter(n, filters)),
    [nodes, filters],
  );

  // Finance-relevant nodes: have budget data OR SP financial data
  const financeNodes = useMemo(
    () => allFiltered.filter(
      (n) => n.budgetProjectName != null || n.iRnD != null || n.fRnD != null || n.estRnD != null || n.estRevenue != null,
    ),
    [allFiltered],
  );

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <div className="space-y-3">
        {/* NPV Parameters (compact) */}
        <div className="flex items-center gap-4 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          <span className="font-medium">NPV Parameters:</span>
          <label className="inline-flex items-center gap-1">
            Discount Rate
            <input
              type="number"
              min={1} max={50} step={0.5}
              value={Math.round(discountRate * 100 * 10) / 10}
              onChange={(e) => {
                const pct = parseFloat(e.target.value);
                if (!isNaN(pct) && pct >= 0 && pct <= 100) {
                  updateResourceConfig({ npvDiscountRate: pct / 100 });
                }
              }}
              className="w-14 px-1.5 py-0.5 rounded text-[11px] text-right tabular-nums"
              style={{ background: 'var(--modal-input-bg)', color: 'var(--text-primary)', border: '1px solid var(--modal-input-border)' }}
            />
            <span>%</span>
          </label>
          <label className="inline-flex items-center gap-1">
            Period
            <select
              value={periodYears}
              onChange={(e) => updateResourceConfig({ npvPeriodYears: parseInt(e.target.value) })}
              className="px-1.5 py-0.5 rounded text-[11px]"
              style={{ background: 'var(--modal-input-bg)', color: 'var(--text-primary)', border: '1px solid var(--modal-input-border)' }}
            >
              <option value={3}>3 years</option>
              <option value={5}>5 years</option>
              <option value={7}>7 years</option>
              <option value={10}>10 years</option>
            </select>
          </label>
          <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
            {financeNodes.length} of {allFiltered.length} nodes with financial data
          </span>
        </div>

        {/* KPI Summary Cards */}
        <FinancialSummaryCards
          nodes={financeNodes}
          resourceConfig={resourceConfig}
          discountRate={discountRate}
          periodYears={periodYears}
        />

        {/* Investment Table */}
        <InvestmentTable
          nodes={financeNodes}
          discountRate={discountRate}
          periodYears={periodYears}
          fteAllocations={fteAllocations}
        />

        {/* Investment Portfolio Bubble Chart */}
        <InvestmentPortfolioChart nodes={financeNodes} />
      </div>
    </div>
  );
}
